import { useMatomo } from '@datapunt/matomo-tracker-react';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { ReactComponent as LogoIcon } from '@assets/svg/logo-icon.svg';
import { ReactComponent as LogoName } from '@assets/svg/logo-name.svg';
import { ReactComponent as ThreeDotsIcon } from '@assets/svg/three-dots.svg';

import Onboarding from '@components/Onboarding/Onboarding';
import Header from '@components/common/Header/Header';
import StatusTypeItem from '@components/common/StatusTypeItem/StatusTypeItem';

import { DEFAULT_CURRENCY } from '@constants/currency';

import useTranslation from '@hooks/useTranslation';

import { useActionItemsStoreAction } from '@pages/ActionItems/store/useActionItemsStore';

import { useActionItemActiveReportQuery } from '@services/actionItem/useActionItemActiveReportQuery';
import { useActionItemQuery } from '@services/actionItem/useActionItemQuery';
import { useActionItemStaticDataQuery } from '@services/actionItem/useActionItemStaticDataQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppPersistOnBoardStart } from '@store/useAppStorePersist';

import ActionItemInfo from './components/ActionItemInfo/ActionItemInfo';
import Activity from './components/Activity/Activity';
import CompletionReportBlock from './components/CompletionReportBlock/CompletionReportBlock';
import Description from './components/Description/Description';
import DownloadAIDetailsMenu from './components/DownloadAIDetailsMenu/DownloadAIDetailsMenu';
import Navigation from './components/Navigation/Navigation';
import NetworkAiExtraBlocks from './components/NetworkAiExtraBlocks/NetworkAiExtraBlocks';
import NodeSensors from './components/NodeSensors/NodeSensors';
import ShortDescription from './components/ShortDescription/ShortDescription';
import TechnicalInfoAI from './components/TechnicalInfoAI/TechnicalInfoAI';
import { MOCKUP_AI } from './constants/mockupAi';
import { getAiControls } from './helpers/getAiControls';
import { getOnboardSteps } from './helpers/getOnboardSteps';
import { getTranslatesNetworkAiTextBlocks } from './helpers/getTranslatesNetworkAiTextBlocks';
import { useActionItemCommentsQuery } from './services/useActionItemCommentsQuery';
import { useActionItemRecommendationsQuery } from './services/useActionItemRecomendationsQuery';
import { selectAITranslateTargetLanguage, useAITranslateActions } from './store/useAITranslateStore';
import style from './style.module.scss';

const DOWNLOAD_MENU_TOOLTIP_ID = 'ai-download-details-context-menu';

const ActionItem = () => {
  const { t } = useTranslation();
  const { trackPageView } = useMatomo();
  const { actionItemId } = useParams();

  const targetLanguage = selectAITranslateTargetLanguage();
  const onBoardStart = selectAppPersistOnBoardStart();
  const steps = getOnboardSteps(t, onBoardStart);
  const [expandedReport, setExpandedReport] = useState<{ [key: number]: boolean }>({});
  const printRef = useRef<HTMLInputElement>(null);

  const { data: profileData } = useUserProfileQuery();
  const dateTimeFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const { data: actionItemData, isFetching: isAiFetching } = useActionItemQuery({
    actionItemId: actionItemId || '',
  });

  const { data: aiStaticData } = useActionItemStaticDataQuery({
    actionItemId: actionItemId || '',
  });

  const { data: commentsData, isFetching: commentsIsFetching } = useActionItemCommentsQuery({
    actionItemId: actionItemId || '',
    dateTimeFormat,
  });

  const { data: activeReportData, isFetching: isActiveReportsFetching } = useActionItemActiveReportQuery({
    actionItemId: actionItemId || '',
  });

  const { data: recommendationsData, isFetching: isRecommendationsFetching } =
    useActionItemRecommendationsQuery({
      actionItemId: actionItemId || '',
    });

  const actionItemInfo = actionItemId === 'onboarding' ? MOCKUP_AI : actionItemData;

  const controls = getAiControls(actionItemInfo, activeReportData, !!profileData?.userAccess.manageData);

  const { setActionItemsViewedId, setRecentlyViewedAIId } = useActionItemsStoreAction();
  const { setAITranslateNetworkAiTextBlocks } = useAITranslateActions();

  useEffect(() => {
    if (actionItemData?.id) {
      setRecentlyViewedAIId(+actionItemData?.id);
      setActionItemsViewedId(+actionItemData?.id);

      if (actionItemData?.networkAiTextBlocks) {
        setAITranslateNetworkAiTextBlocks(getTranslatesNetworkAiTextBlocks(actionItemData, targetLanguage));
      }
    }
  }, [actionItemData?.id]);

  useEffect(() => {
    trackPageView({});
  }, []);

  const showDescription =
    !actionItemData?.networkAiTextBlocks &&
    (!!actionItemInfo?.hasDescription || !!aiStaticData?.actionListChartsImages?.length);
  const userTimezone = -new Date().getTimezoneOffset();
  const showOnboarding = !isAiFetching && !isActiveReportsFetching && actionItemInfo;
  const costSavingTemplateCurrency = aiStaticData?.costSavingTemplateCurrency || null;

  return (
    <div className={style.pageAiDetails} ref={printRef}>
      <Header
        title={t('Action item details')}
        isSecondaryPage={true}
        extraInfo={
          <ActionItemInfo
            aiDateTime={actionItemInfo?.time_created}
            createdBy={actionItemInfo?.created_by}
            userTimezone={userTimezone}
          />
        }
        headerLeftComponent={
          actionItemInfo && <StatusTypeItem item={actionItemInfo} onlyTitle={true} showSnoozeAlways={true} />
        }
        headerRightComponent={
          <>
            <Navigation isArchivedAi={actionItemData?.stateName === 'Archived'} />
            <div className={style.actionsIconContainer} data-tooltip-id={DOWNLOAD_MENU_TOOLTIP_ID}>
              <ThreeDotsIcon />
            </div>
          </>
        }
      />
      <DownloadAIDetailsMenu
        id={DOWNLOAD_MENU_TOOLTIP_ID}
        actionItemData={actionItemData}
        commentsInfo={commentsData?.commentsInfo}
        recommendationsInfo={recommendationsData?.info}
        printRef={printRef}
      />
      <div className={style.printHeader}>
        <LogoIcon className={style.logoIcon} />
        <LogoName className={style.logoName} />
      </div>
      <div className={style.pageAiDetailsBody}>
        <div className={style.informationBlock}>
          <TechnicalInfoAI actionItem={actionItemInfo} controls={controls} />
          <NodeSensors actionItem={actionItemInfo} />
          {!!recommendationsData?.list?.length && (
            <ShortDescription
              id={!showDescription ? 'tourStep-14' : undefined}
              recommendationsData={recommendationsData}
              isFetching={isRecommendationsFetching}
            />
          )}
          <CompletionReportBlock
            controls={controls}
            userTimezone={userTimezone}
            expandedReport={expandedReport}
            setExpandedReport={setExpandedReport}
            currency={actionItemInfo?.facility.currency || DEFAULT_CURRENCY}
            costSavingTemplateCurrency={costSavingTemplateCurrency}
          />
          {showDescription && (
            <Description actionItem={actionItemInfo} chartImages={aiStaticData?.actionListChartsImages} />
          )}
          {actionItemData?.networkAiTextBlocks && (
            <NetworkAiExtraBlocks
              actionItem={actionItemData}
              procedureInfo={actionItemData?.networkAiTextBlocks}
            />
          )}
        </div>
        <Activity
          actionItem={actionItemInfo}
          controls={controls}
          commentsData={commentsData}
          isCommentsLoading={!commentsData || commentsIsFetching}
        />
        {showOnboarding && <Onboarding steps={steps} />}
      </div>
    </div>
  );
};

export default ActionItem;
